import React, { useEffect } from "react"
import { Search } from "lucide-react"

import { HELP_TEXT, TAP_TARGET } from "@/lib/ui-tokens"
import { cn } from "@/lib/utils"
import { Checkbox } from "@/shadcn/components/ui/checkbox"
import { Input } from "@/shadcn/components/ui/input"
import { Label } from "@/shadcn/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shadcn/components/ui/select"
import {
  ToggleGroup,
  ToggleGroupItem,
} from "@/shadcn/components/ui/toggle-group"
import { SourceChip } from "./marks"
import { optionPairs } from "./options"
import { type ConfigureField, type ConfigureSpec } from "./types"
import { useRecordEvent } from "@/lib/use-record-event"

type ControlProps = {
  field: ConfigureField
  /** Resolved by the caller so each control doesn't re-derive them. */
  options: { value: string; label: string }[]
  onFieldChange?: (field: ConfigureField, value: string) => void
}

/** The segmented control the Figma frames use for a short set of choices. */
function ToggleControl({ field, options, onFieldChange }: ControlProps) {
  return (
    <ToggleGroup
      type="single"
      spacing={0}
      variant="outline"
      /* "" rather than undefined for a field with no value yet: undefined makes
         a Radix ToggleGroup uncontrolled, so it would start tracking its own
         selection and drift from the spec. An empty string matches no item, so
         nothing reads as chosen — which is the truth, and what the select this
         replaced showed as its placeholder. */
      value={field.value ?? ""}
      onValueChange={
        onFieldChange
          ? (value) => value && onFieldChange(field, value)
          : undefined
      }
      disabled={field.disabled}
      className="tw:w-fit tw:max-w-full tw:flex-wrap"
    >
      {options.map((option) => (
        <ToggleGroupItem
          key={option.value}
          value={option.value}
          className={TAP_TARGET}
        >
          {option.label}
        </ToggleGroupItem>
      ))}
    </ToggleGroup>
  )
}

/** The same choice as a dropdown, for option sets too long to sit in a row. */
function SelectControl({ field, options, onFieldChange }: ControlProps) {
  return (
    <Select
      value={field.value}
      onValueChange={
        onFieldChange ? (value) => onFieldChange(field, value) : undefined
      }
      disabled={field.disabled}
    >
      {/* h-11! / md:h-8! rather than TAP_TARGET: SelectTrigger sizes itself
          with `data-[size=default]:h-8`, a class+attribute selector at
          specificity (0,2,0), so a plain h-11 (0,1,0) loses to it whatever the
          source order. w-full needs no `!` — it and the primitive's w-fit are
          one tailwind-merge group. */}
      <SelectTrigger className="tw:h-11! tw:w-full tw:md:h-8!">
        <SelectValue placeholder={field.placeholder} />
      </SelectTrigger>
      {/* No `tw-root` here, unlike the DropdownMenuContents elsewhere in the
          flow: SelectContent's primitive already wraps its own portal in one. */}
      <SelectContent>
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

/**
 * Step 1 · configure the connected source.
 *
 * Data-driven: the caller supplies the fields, so the input types that need
 * extra touches each describe their own form rather than this file carrying a
 * branch per network. `onFieldChange` identifies the field by its spec object,
 * which is what the caller already holds.
 */

export function ConfigureStep({
  spec,
  base,
  onFieldChange,
}: {
  spec: ConfigureSpec
  base: string
  /**
   * Omit to render the form read-only, as the design harness does.
   *
   * For a "checkbox" field `value` is the option that was clicked, not the new
   * selection — see ConfigureField.values.
   */
  onFieldChange?: (field: ConfigureField, value: string) => void
}) {
  // Checkbox labels need ids that are unique to this instance, not just to the
  // field: two configure steps on one page would otherwise cross-wire their
  // labels to each other's inputs.
  const uid = React.useId()
  const recordEvent = useRecordEvent()

  // Mount-only: this reports that the step was reached, so it fires once even
  // if the caller swaps the spec while the step stays open.
  useEffect(() => {
    recordEvent("Automation Building - Configure Social Input", {
      network: spec.account.network
    });
  }, []);

  return (
    <div className="tw:flex tw:w-full tw:flex-col tw:gap-6">
      <SourceChip source={spec.account} base={base} />
      {spec.fields.map((field) => {
        const kind = field.kind ?? (field.options ? "toggle" : "input")
        const options = optionPairs(field.options)
        const control = { field, options, onFieldChange }
        return (
          <div
            key={field.label}
            className={cn(
              "tw:flex tw:flex-col tw:gap-2",
              // Figma: Field 384 wide. The search card is drawn 617 there, but
              // it's the same control as the RSS search on the find step, which
              // is full-width — so it matches that instead of the Figma width.
              kind === "search"
                ? "tw:w-full"
                : kind === "segmented"
                  // Uncapped on desktop with the designer's agreement: these
                  // option labels are sentences, and 384px is what forced them
                  // into a dropdown in the first place. The cap still applies at
                  // mobile, where the control is the select anyway.
                  ? "tw:max-w-[384px] tw:md:max-w-none"
                  : "tw:max-w-[384px]"
            )}
          >
            <p className="tw:text-sm tw:font-medium tw:text-foreground">
              {field.label}
              {field.required ? (
                <span aria-hidden className="tw:ml-0.5 tw:text-destructive">
                  *
                </span>
              ) : null}
            </p>

            {kind === "toggle" ? (
              <ToggleControl {...control} />
            ) : kind === "segmented" ? (
              /* Both controls are rendered and CSS picks one, rather than a JS
                 breakpoint hook choosing: `display: none` also takes the hidden
                 one out of the tab order, and there is no first-paint guess to
                 correct once a resize listener reports the real width. */
              <>
                <div className="tw:hidden tw:md:block">
                  <ToggleControl {...control} />
                </div>
                <div className="tw:md:hidden">
                  <SelectControl {...control} />
                </div>
              </>
            ) : kind === "select" ? (
              <SelectControl {...control} />
            ) : kind === "search" ? (
              <div className="tw:rounded-lg tw:border tw:border-border tw:bg-card tw:p-4 tw:shadow-sm">
                <div className="tw:relative">
                  <Search className="tw:pointer-events-none tw:absolute tw:top-1/2 tw:left-2.5 tw:size-4 tw:-translate-y-1/2 tw:text-muted-foreground" />
                  <Input
                    readOnly={!onFieldChange}
                    disabled={field.disabled}
                    value={onFieldChange ? (field.value ?? "") : undefined}
                    defaultValue={onFieldChange ? undefined : field.value}
                    onChange={
                      onFieldChange
                        ? (event) => onFieldChange(field, event.target.value)
                        : undefined
                    }
                    placeholder={field.placeholder}
                    className={cn(TAP_TARGET, "tw:pl-8")}
                  />
                </div>
              </div>
            ) : kind === "checkbox" ? (
              /* A row that wraps rather than a column: prod draws these three
                 inline, and at 384px "Feed Posts / Reels / Stories" fits on one
                 line with room to spare. gap-x-5 keeps the hit areas apart —
                 the primitive's own ::after grows each box by 12px either side,
                 so a tighter gap would have neighbouring labels stealing
                 clicks. */
              <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-x-5 tw:gap-y-2.5">
                {options.map((option) => {
                  const id = `${uid}-${field.name ?? field.label}-${option.value}`
                  return (
                    <div
                      key={option.value}
                      className="tw:flex tw:items-center tw:gap-2"
                    >
                      <Checkbox
                        id={id}
                        checked={(field.values ?? []).includes(option.value)}
                        disabled={field.disabled}
                        onCheckedChange={
                          onFieldChange
                            ? () => onFieldChange(field, option.value)
                            : undefined
                        }
                      />
                      <Label
                        htmlFor={id}
                        className="tw:text-sm tw:font-normal tw:text-foreground"
                      >
                        {option.label}
                      </Label>
                    </div>
                  )
                })}
              </div>
            ) : (
              <Input
                readOnly={!onFieldChange}
                disabled={field.disabled}
                value={onFieldChange ? (field.value ?? "") : undefined}
                defaultValue={onFieldChange ? undefined : field.value}
                onChange={
                  onFieldChange
                    ? (event) => onFieldChange(field, event.target.value)
                    : undefined
                }
                placeholder={field.placeholder}
                className={TAP_TARGET}
              />
            )}

            {field.error}
            {field.help ? <p className={HELP_TEXT}>{field.help}</p> : null}
          </div>
        )
      })}
    </div>
  )
}
